import { ProductContentType, ProductV2, formatPriceText, GameId, isGemPackOffer } from '@common'
import cx from 'classnames'
import Image, { StaticImageData } from 'next/image'

import CocGemIcon from '@/assets/images/icons/coc_icon_gem.png'
import CrGemIcon from '@/assets/images/icons/cr_icon_gem.png'
import BgFlash from '@/assets/images/storefronts/brawlstars/bg-flash.png'
import BgFlare from '@/assets/images/storefronts/clashofclans/shop_item_bg_flare.png'
import { useGameImages } from '@/components/games/images'
import { useLocale } from '@/features/locale/useLocale'
import { useTranslatable } from '@/localization/useTranslatable'
import { isProductImageEmpty, resolveProductImage } from '@/utils/resolveProductImage'
import { useSnowplowProductCardViewImpression } from '@/utils/snowplow'
import { useAppSelector } from '@/utils/useAppSelector'

import { useGameSlug } from '../../game-provider'
import { BuyButton } from '../BuyButton/BuyButton'
import { ProductRibbon } from '../cardRibbon/ProductRibbon'
import { StoreTypography } from '../typography/StoreTypography'
import { CardBase } from './CardBase'
import { useHandleClick } from './useHandleClick'
import { useFormattedAmount, useSmallerFont } from './utils'

import base from './cardBase.module.scss'
import styles from './cardSingleProduct.module.scss'

// TODO: Add Skins, Sceneries, etc
export type ProductCardContentType = Extract<ProductContentType, 'GEM' | 'SEASON_PASS'>

type CardSingleProductProps = {
  textScaling?: 'Small' | 'XSmall' | 'XXSmall'
  children?: React.ReactNode
  game?: GameId
  product: ProductV2
  productType: ProductCardContentType
}

const TitleSegment = ({
  title,
  count,
  productType,
  gameSlug,
}: {
  title: string
  count: number
  productType: ProductCardContentType
  gameSlug: GameId
}) => {
  const gemIcon = gameSlug === 'clashroyale' ? CrGemIcon : CocGemIcon
  const icons: Record<ProductCardContentType, StaticImageData> = {
    GEM: gemIcon,
    SEASON_PASS: gemIcon,
  } as const

  const icon = icons[productType]
  const formattedAmount = useFormattedAmount(count)
  const smallerFont = useSmallerFont({ contentAmount: count })

  return (
    <>
      <StoreTypography tag="h3" style="outlined" textSize="sm" className={cx(styles.title, styles[gameSlug])}>
        {title}
      </StoreTypography>
      <div className={styles.subtitle}>
        <div className={styles.Icon}>
          <Image src={icon} alt="gem" fill sizes="50px" />
        </div>
        {count && (
          <StoreTypography
            tag="div"
            textSize={smallerFont ? 'xs' : 'body'}
            style="outlined"
            className={styles.GemCount}
          >
            {formattedAmount}
          </StoreTypography>
        )}
      </div>
    </>
  )
}

const BrawlTitleSegment = ({ count }: { count: number }) => {
  const formattedAmount = useFormattedAmount(count)

  return (
    <StoreTypography tag="div" textSize="md" style="outlined" className={styles.brawlTitleSegment}>
      {count && <span>x{formattedAmount}</span>}
    </StoreTypography>
  )
}

export const CardSingleProduct = ({ product, productType, textScaling }: CardSingleProductProps) => {
  const gameSlug = useGameSlug()
  const locale = useLocale()
  const pathname = `/game/${gameSlug}/product/${product.slug}`

  const { handleClick } = useHandleClick({ product, pathname })
  const translatable = useTranslatable()
  const market = useAppSelector((state) => state.config.market)
  const { gameImage } = useGameImages(gameSlug)

  const brawl = gameSlug === 'brawlstars'
  // Currency is not available if country is not supported
  const currencyCountry = market?.currencyCountry
  const currency = market?.currencyCode

  const title = product.title
  const count = product.contentCounts.GEM
  const productImage = !isProductImageEmpty(product)
    ? resolveProductImage(product)
    : gameImage('missingProductImageSmall')
  const hasProductData = title && count && productImage

  const CrGemPackOffer = isGemPackOffer(product)

  const snowplowViewImpressionRef = useSnowplowProductCardViewImpression<HTMLDivElement>(
    product,
    'CardSingleProduct',
    'CardSingleProduct',
  )

  if (!hasProductData) {
    return null
  }

  return (
    <CardBase
      href={pathname}
      className={cx(base.cardSingleProduct, base[gameSlug], styles[gameSlug], {
        [base.CrGemPackOffer]: CrGemPackOffer,
        [styles.CrGemPackOffer]: CrGemPackOffer,
      })}
      onClick={handleClick}
    >
      <>
        <ProductRibbon product={product} position="s" />
        <div className={cx(styles.bgContainer)} ref={snowplowViewImpressionRef}>
          {brawl ? (
            <Image src={BgFlash} alt="product flash" fill style={{ objectFit: 'cover' }} sizes="200px" />
          ) : (
            <Image
              src={BgFlare}
              alt="product flare"
              fill
              style={{ objectFit: 'contain' }}
              sizes="200px"
              className={styles.bgFlare}
            />
          )}
        </div>
        <div className={cx(styles.cardContent, styles[gameSlug])}>
          {brawl ? (
            <BrawlTitleSegment count={count} />
          ) : (
            <TitleSegment
              title={translatable(product.title, 'title')}
              count={count}
              productType={productType}
              gameSlug={gameSlug}
            />
          )}
          <div className={styles.imageContainer}>
            <Image src={productImage} alt={translatable(product.title)} fill quality={100} sizes="200px" />
          </div>
        </div>
        <div className={cx(styles.cardFooter, { [styles.fullWidth]: !brawl })}>
          <BuyButton
            type="Primary"
            product={product}
            content={currencyCountry ? formatPriceText(product.prices[currencyCountry], locale, currency) : ''}
            textScaling={textScaling}
          />
        </div>
      </>
    </CardBase>
  )
}

CardSingleProduct.displayName = 'CardSingleProduct'
